import React, { useState } from 'react';
import { ChevronUp, ChevronDown, User, Briefcase } from 'lucide-react';

const AssignmentDecisionLogic = ({
    rulesFired,
    inputParams,
    triageResult,
    assignmentData,
    adjusterRegion,
    handlerRegion
}) => {
    const [isHierarchyExpanded, setIsHierarchyExpanded] = useState(false);

    // Helper function to properly evaluate GLE flag as boolean
    const isGLEActive = () => {
        const gleValue = String(inputParams.gleFlag || '').trim().toLowerCase();
        // Consider GLE active if it has a meaningful value (not empty, null, N/A, false, no)
        if (!gleValue || gleValue === '' || gleValue === 'null' || gleValue === 'undefined') return false;
        if (gleValue === 'n/a' || gleValue === 'na' || gleValue === 'none') return false;
        if (gleValue === 'false' || gleValue === 'no') return false;
        // Any other non-empty value is considered active
        return true;
    };

    // Helper function to get GLE display value
    const getGLEDisplayValue = () => {
        if (!isGLEActive()) return 'FALSE';
        const gleValue = String(inputParams.gleFlag || '').trim();
        // If it's a boolean true or 'true' string, show TRUE
        if (gleValue.toLowerCase() === 'true' || gleValue.toLowerCase() === 'yes') return 'TRUE';
        // Otherwise show the actual description
        return gleValue;
    };

    // Helper function to check if high risk industry
    const isHighRiskIndustry = () => {
        return inputParams.industryCode?.toLowerCase().includes('construction') ||
            inputParams.industryCode?.toLowerCase().includes('oil') ||
            inputParams.industryCode?.toLowerCase().includes('gas') ||
            inputParams.industryCode?.toLowerCase().includes('mining') ||
            triageResult?.claim?.industryRisk === 'HIGH';
    };

    // Helper function to check if complex scope
    const isComplexScope = () => {
        return inputParams.scopeCode === 'SC3' || inputParams.scopeCode === 'SC4';
    };

    if (!rulesFired || rulesFired.length === 0) {
        return null; // Or handle empty state appropriately
    }

    return (
        <>
            {/* Assignment Logic Section */}
            <section className="card triage-card mb-2 px-2">
                <div className="card-body">
                    <h5 className="triage-card-title mb-3">Assignment Logic</h5>

                    {/* Input Summary */}
                    <div className="mb-4">
                        <h6 className="fw-bold mb-2" style={{ fontSize: '0.95rem' }}>
                            Input Summary
                        </h6>
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '30%' }}>Parameter</th>
                                        <th style={{ width: '25%' }}>Value</th>
                                        <th style={{ width: '45%' }}>Impact on Decision</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="fw-semibold">Line of Business</td>
                                        <td>{inputParams.lob}</td>
                                        <td>Eligible for Property Rules</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Region</td>
                                        <td>{triageResult?.claim?.region || inputParams.region}</td>
                                        <td>Determines Regional Pool Selection</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Estimated Loss</td>
                                        <td>${parseFloat(inputParams.estimatedLoss || 0).toLocaleString()}</td>
                                        <td>
                                            {parseFloat(inputParams.estimatedLoss || 0) < 50000 && 'Falls in <$50K Band (JR Level)'}
                                            {parseFloat(inputParams.estimatedLoss || 0) >= 50000 && parseFloat(inputParams.estimatedLoss || 0) <= 250000 && 'Falls in $50K-$250K Band (Experienced Level)'}
                                            {parseFloat(inputParams.estimatedLoss || 0) > 250000 && 'Falls in >$250K Band (Senior Level)'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Scope Code</td>
                                        <td>{inputParams.scopeCode}</td>
                                        <td>
                                            {(inputParams.scopeCode === 'SC3' || inputParams.scopeCode === 'SC4')
                                                ? 'Complex Claim - Escalation Required'
                                                : 'Standard Scope - No Escalation'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Business Segment</td>
                                        <td>
                                            {inputParams.businessSegment === 'SMALL' && 'Small Business'}
                                            {inputParams.businessSegment === 'MID_CORP' && 'Mid-Market Corporate'}
                                            {inputParams.businessSegment === 'LCR' && 'Large Corporate (LCR)'}
                                        </td>
                                        <td>
                                            {inputParams.businessSegment === 'SMALL' && 'Baseline → General Commercial Handler'}
                                            {inputParams.businessSegment === 'MID_CORP' && 'Baseline → Segment Specific Handler'}
                                            {inputParams.businessSegment === 'LCR' && 'Baseline → Dedicated Account Handler'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Industry</td>
                                        <td>{inputParams.industryCode || 'Not Specified'}</td>
                                        <td>
                                            {triageResult?.claim?.industryRisk === 'HIGH' && 'High Risk - May Trigger Escalation/Specialist'}
                                            {triageResult?.claim?.industryRisk === 'MEDIUM' && 'Medium Risk - Standard Processing'}
                                            {triageResult?.claim?.industryRisk === 'LOW' && 'Low Risk - Standard Processing'}
                                            {!triageResult?.claim?.industryRisk && 'Risk Level Not Evaluated'}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Global Loss Event (GLE)</td>
                                        <td>{getGLEDisplayValue()}</td>
                                        <td>
                                            {isGLEActive()
                                                ? 'Catastrophe Override Triggered - CAT Adjuster Required'
                                                : 'No Catastrophe Override'}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>


            {/* Rule Evaluation Hierarchy */}
            <section className="card triage-card mb-2 px-2">
                <div
                    className="card-body d-flex justify-content-between align-items-center py-3"
                    onClick={() => setIsHierarchyExpanded(!isHierarchyExpanded)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <div>
                            <h5 className="triage-card-title mb-0">Rule Evaluation</h5>
                            {!isHierarchyExpanded && (
                                <small className="text-muted d-block fw-normal mt-1">
                                    Click to view how rules were evaluated
                                </small>
                            )}
                        </div>
                    </div>
                    <div className="text-muted d-flex align-items-center gap-2">
                        <small>{isHierarchyExpanded ? 'Hide Details' : 'View Details'}</small>
                        {isHierarchyExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                </div>

                {isHierarchyExpanded && (
                    <div className="card-body pt-0">
                        <p className="text-muted mb-0 mb-md-3 mb-lg-3 ms-1">
                            Assignments are determined by evaluating rules in priority order. The first applicable rule sets the assignment.
                        </p>

                        <div className="row g-3 g-lg-5">
                            {/* Adjuster Rules Column */}
                            <div className="col-lg-6">
                                <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
                                    <h6 className="fw-bold mb-0 text-dark">Claim Adjuster Rules</h6>
                                </div>
                                <div className="d-flex flex-column gap-3">
                                    {/* Priority 1: GLE Flag */}
                                    <div className="d-flex position-relative">
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className={`fw-semibold ${isGLEActive() ? '' : 'text-muted'}`}
                                                    style={{ color: isGLEActive() ? '#006BB3' : undefined, fontSize: '0.9rem' }}>
                                                    1. Catastrophe Override
                                                </span>
                                                {isGLEActive() && <span className="badge rounded-pill fw-normal" style={{ backgroundColor: '#e6f2ff', color: '#006BB3', border: '1px solid #b3d7ff' }}>Applied</span>}
                                            </div>
                                            <div className="small text-muted">
                                                If GLE flag is TRUE → Assign CAT Adjuster
                                            </div>
                                        </div>
                                    </div>

                                    {/* Priority 2: Scope Complexity */}
                                    <div className="d-flex position-relative">
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className={`fw-semibold ${(!isGLEActive() && isComplexScope()) ? '' : 'text-muted'}`}
                                                    style={{ color: (!isGLEActive() && isComplexScope()) ? '#006BB3' : undefined, fontSize: '0.9rem' }}>
                                                    2. Scope Complexity
                                                </span>
                                                {(!isGLEActive() && isComplexScope()) && (
                                                    <span className="badge rounded-pill fw-normal" style={{ backgroundColor: '#e6f2ff', color: '#006BB3', border: '1px solid #b3d7ff' }}>Applied</span>
                                                )}
                                                {isGLEActive() && (
                                                    <span className="badge rounded-pill fw-normal text-muted border bg-light">Overridden</span>
                                                )}
                                            </div>
                                            <div className="small text-muted">
                                                If Scope is SC3 or SC4 → Escalate to SENIOR Adjuster
                                            </div>
                                        </div>
                                    </div>

                                    {/* Priority 3: Standard Assessment */}
                                    <div className="d-flex position-relative">
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className={`fw-semibold ${(!isGLEActive() && !isComplexScope()) ? '' : 'text-muted'}`}
                                                    style={{ color: (!isGLEActive() && !isComplexScope()) ? '#006BB3' : undefined, fontSize: '0.9rem' }}>
                                                    3. Standard Assessment
                                                </span>
                                                {(!isGLEActive() && !isComplexScope()) && (
                                                    <span className="badge rounded-pill fw-normal" style={{ backgroundColor: '#e6f2ff', color: '#006BB3', border: '1px solid #b3d7ff' }}>Applied</span>
                                                )}
                                                {(isGLEActive() || isComplexScope()) && (
                                                    <span className="badge rounded-pill fw-normal text-muted border bg-light">Overridden</span>
                                                )}
                                            </div>
                                            <div className="small text-muted">
                                                Based on Estimated Loss & Industry Risk Level
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Handler Rules Column */}
                            <div className="col-lg-6">
                                <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
                                    <h6 className="fw-bold mb-0 text-dark">Claim Handler Rules</h6>
                                </div>
                                <div className="d-flex flex-column gap-3">
                                    {/* Priority 1: Industry Specialist */}
                                    <div className="d-flex position-relative">
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className={`fw-semibold ${isHighRiskIndustry() ? '' : 'text-muted'}`}
                                                    style={{
                                                        color: isHighRiskIndustry() ? '#006BB3' : undefined, fontSize: '0.9rem'
                                                    }}>
                                                    1. Industry Specialist
                                                </span>
                                                {isHighRiskIndustry() && <span className="badge rounded-pill fw-normal" style={{ backgroundColor: '#e6f2ff', color: '#006BB3', border: '1px solid #b3d7ff' }}>Applied</span>}
                                            </div>
                                            <div className="small text-muted">
                                                High Risk Industries require Specialist Handler
                                            </div>
                                        </div>
                                    </div>

                                    {/* Priority 2: Scope Complexity */}
                                    <div className="d-flex position-relative">
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className={`fw-semibold ${(!isHighRiskIndustry() && isComplexScope()) ? '' : 'text-muted'}`}
                                                    style={{
                                                        color: (!isHighRiskIndustry() && isComplexScope()) ? '#006BB3' : undefined, fontSize: '0.9rem'
                                                    }}>
                                                    2. Scope Complexity
                                                </span>
                                                {(!isHighRiskIndustry() && isComplexScope()) && (
                                                    <span className="badge rounded-pill fw-normal" style={{ backgroundColor: '#e6f2ff', color: '#006BB3', border: '1px solid #b3d7ff' }}>Applied</span>
                                                )}
                                                {isHighRiskIndustry() && (
                                                    <span className="badge rounded-pill fw-normal text-muted border bg-light">Overridden</span>
                                                )}
                                            </div>
                                            <div className="small text-muted">
                                                Complex Scope (SC3/SC4) → Dedicated Account Handler
                                            </div>
                                        </div>
                                    </div>

                                    {/* Priority 3: Business Segment Baseline */}
                                    <div className="d-flex position-relative">
                                        <div className="flex-grow-1">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className={`fw-semibold ${(!isHighRiskIndustry() && !isComplexScope()) ? '' : 'text-muted'}`}
                                                    style={{
                                                        color: (!isHighRiskIndustry() && !isComplexScope()) ? '#006BB3' : undefined, fontSize: '0.9rem'
                                                    }}>
                                                    3. Business Segment Baseline
                                                </span>
                                                {(!isHighRiskIndustry() && !isComplexScope()) && (
                                                    <span className="badge rounded-pill fw-normal" style={{ backgroundColor: '#e6f2ff', color: '#006BB3', border: '1px solid #b3d7ff' }}>Applied</span>
                                                )}
                                                {(isHighRiskIndustry() || isComplexScope()) && (
                                                    <span className="badge rounded-pill fw-normal text-muted border bg-light">Overridden</span>
                                                )}
                                            </div>
                                            <div className="small text-muted">
                                                Standard assignment based on Business Segment
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Outcome Summary */}
                        <div className="mt-4 pb-2">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="p-3 bg-white h-100 d-flex align-items-center gap-3 shadow-sm border" style={{ borderRadius: '10px', borderLeft: '4px solid #006BB3' }}>
                                        <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '42px', height: '42px', backgroundColor: '#e6f2ff' }}>
                                            <User size={20} color="#006BB3" />
                                        </div>
                                        <div>
                                            <div className="text-uppercase fw-bold text-muted tracking-wide mb-1" style={{ fontSize: '0.65rem' }}>Adjuster Assigned</div>
                                            <h6 className="mb-1 fw-bold text-dark" style={{ fontSize: '0.95rem' }}>{assignmentData?.adjuster?.name || 'Not Assigned'}</h6>
                                            <div className="text-muted lh-1" style={{ fontSize: '0.75rem' }}>
                                                {assignmentData?.adjuster?.level || 'N/A'} <span className="mx-1">•</span> {assignmentData?.adjuster?.region || adjusterRegion || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="p-3 bg-white h-100 d-flex align-items-center gap-3 shadow-sm border" style={{ borderRadius: '10px', borderLeft: '4px solid #006BB3' }}>
                                        <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '42px', height: '42px', backgroundColor: '#e6f2ff' }}>
                                            <Briefcase size={20} color="#006BB3" />
                                        </div>
                                        <div>
                                            <div className="text-uppercase fw-bold text-muted tracking-wide mb-1" style={{ fontSize: '0.65rem' }}>Handler Assigned</div>
                                            <h6 className="mb-1 fw-bold text-dark" style={{ fontSize: '0.95rem' }}>{assignmentData?.handler?.name || 'Not Assigned'}</h6>
                                            <div className="text-muted lh-1" style={{ fontSize: '0.75rem' }}>
                                                {assignmentData?.handler?.type?.replace(/_/g, ' ') || 'N/A'} <span className="mx-1">•</span> {assignmentData?.handler?.region || handlerRegion || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </>
    );
};

export default AssignmentDecisionLogic;